import React, { useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { Badge } from '../components/Badge'
import { buyerDownloadProvisioningLoa, getBuyerProvisioning, submitProvisioningDetails } from '../api/api-client'
import { useBuyerProvisionings } from '../hooks/useQuoteRequests'
import { ProvisioningTimeline } from '../components/ProvisioningTimeline'
import { popLabel } from '../constants/pops'
import type { ProvisioningView } from '../api/schema'

// ─── Page ─────────────────────────────────────────────────────────────────────

function formatProtectionPeriod(provisioning: ProvisioningView): string {
  const frmt = (d: Date) => {
    return d.toLocaleDateString('en-SG', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' })
  }

  const from = new Date(provisioning.period_from)
  const until = new Date(provisioning.period_until)
  return `${frmt(from)} → ${frmt(until)} `
}

export const BuyerProvisioning: React.FC = () => {
  const { provisionings, loading: provLoading, error, refresh } = useBuyerProvisionings()
  const [selectedProv,  setSelectedProv]  = useState<ProvisioningView | null>(null)
  const [provRefreshKey, setProvRefreshKey] = useState(0)

  if (selectedProv) {
    return (
      <ProvisioningTimeline
        key={provRefreshKey}
        onRefresh={async () => {
          setSelectedProv(await getBuyerProvisioning(selectedProv.id))
          setProvRefreshKey(k => k + 1)
        }}
        provisioningId={selectedProv.id}
        status={selectedProv.status}
        location={popLabel(selectedProv.location_id)}
        dirtyBw={selectedProv.dirty_bandwidth_gbps}
        cleanBw={selectedProv.clean_bandwidth_gbps}
        periodFrom={selectedProv.period_from}
        periodUntil={selectedProv.period_until}
        history={selectedProv.submissions.map(s => ({
          id:             s.id,
          submitted_at:   s.submitted_at,
          status:         s.status,
          decline_reason: s.decline_reason,
          documents:      [],
          details:        s.details,
        }))}
        role="buyer"
        showHeader={true}
        onBack={() => setSelectedProv(null)}
        onSubmit={async (details) => submitProvisioningDetails(selectedProv.id, details)}
        downloadLoa={async (submission_id: number) => buyerDownloadProvisioningLoa(selectedProv.id, submission_id)}
        
        ></ProvisioningTimeline>
    )
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'1.25rem' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <h2 style={{ margin:0, fontSize:'1rem', fontWeight:500, color:'var(--color-text-primary)' }}>
          Provisioning
        </h2>
        <button onClick={refresh} style={{
          display:'flex', alignItems:'center', gap:'0.375rem',
          padding:'0.375rem 0.75rem', border:'1px solid var(--color-border-secondary)',
          borderRadius:'0.375rem', background:'var(--color-background-primary)',
          fontSize:'0.75rem', color:'var(--color-text-secondary)', cursor:'pointer'
        }}>
          <RefreshCw size={13}/> Refresh
        </button>
      </div>

      {error && (
        <div style={{ borderRadius:'0.5rem', padding:'0.75rem 1rem', fontSize:'0.875rem',
          background:'#FEF2F2', color:'#B91C1C', border:'1px solid #FECACA' }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div style={{ ...card, padding:0 }}>
        {provLoading && (
          <p style={{ padding:'1.25rem', fontSize:'0.75rem', color:'var(--color-text-tertiary)' }}>
            Loading…
          </p>
        )}
        {!provLoading && (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.875rem' }}>
            <thead>
              <tr style={{ fontSize:'0.75rem', color:'var(--color-text-secondary)',
                borderBottom:'1px solid var(--color-border-secondary)',
                background:'var(--color-background-secondary)' }}>
                {['Location', 'Vendor', 'Protection Period','Dirty Bandwidth','Clean Bandwidth',
                  'Provisioning','Latest Submission',''].map(h => (
                  <th key={h} style={{ padding:'0.625rem 0.75rem', textAlign:'left',
                    fontWeight:500, whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {provisionings.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding:'2rem', textAlign:'center',
                    fontSize:'0.75rem', color:'var(--color-text-tertiary)' }}>
                    No provisioning records yet.
                  </td>
                </tr>
              ) : (
                provisionings.map(prov => {
                  const latestSub = prov.submissions[prov.submissions.length -1]
                  return (
                    <tr key={prov.id}
                      onClick={() => setSelectedProv(prov)}
                      onMouseEnter={(e: React.MouseEvent<HTMLTableRowElement>) => {
                        e.currentTarget.style.background = 'var(--color-background-secondary)'
                      }}
                      onMouseLeave={(e: React.MouseEvent<HTMLTableRowElement>) => {
                        e.currentTarget.style.background = 'transparent'
                      }}
                      style={{ borderBottom:'1px solid var(--color-border-tertiary)', cursor:'pointer' }}
                    >
                      <td style={{ padding:'0.75rem', fontSize:'0.8rem',
                        color:'var(--color-text-primary)' }}>
                        {popLabel(prov.location_id)}
                      </td>
                      <td style={{ padding:'0.75rem', fontSize:'0.8rem',
                        color:'var(--color-text-primary)' }}>
                        {prov.seller_name}
                      </td>
                      <td style={{ padding:'0.75rem', fontSize:'0.75rem',
                        fontFamily:'var(--font-mono)', color:'var(--color-text-secondary)',
                        whiteSpace:'nowrap' }}>
                          {formatProtectionPeriod(prov)}
                      </td>
                      <td style={{ padding:'0.75rem', fontSize:'0.8rem',
                        color:'var(--color-text-secondary)' }}>
                        {prov.dirty_bandwidth_gbps} Gbps
                      </td>
                      <td style={{ padding:'0.75rem', fontSize:'0.8rem',
                        color:'var(--color-text-success)', fontWeight:500 }}>
                        {prov.clean_bandwidth_gbps} Gbps
                      </td>
                      <td style={{ padding:'0.75rem' }}>
                        <Badge status={prov.status} />
                      </td>
                      <td style={{ padding:'0.75rem' }}>
                        {latestSub ? (
                          <Badge status={latestSub.status} />
                        ) : (
                          <span style={{ fontSize:'0.75rem', color:'var(--color-text-tertiary)' }}>
                            No submissions yet
                          </span>
                        )}
                      </td>
                      <td style={{ padding:'0.75rem', fontSize:'0.75rem',
                        color:'var(--color-text-tertiary)' }}>
                        View →
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        )}
      </div>

    </div>
  )
}

const card: React.CSSProperties = {
  background:'#fff', borderRadius:'0.75rem',
  border:'1px solid #E2E8F0', overflow:'hidden',
}